import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  DISCOVERY_PERMISSIONS,
  KEY_ROTATION_DAYS,
  discoveryPolicyDocument,
  type CloudConnection,
  type CloudConnectionStatus,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../auth/auth';
import { ProposalsPanel } from './ProposalsPanel';

const STATUS: Record<CloudConnectionStatus, { label: string; cls: string }> = {
  PENDING: { label: 'Checking…', cls: 'status-waiting' },
  VERIFIED: { label: 'Verified read-only', cls: 'status-done' },
  REJECTED: { label: 'Refused', cls: 'status-active' },
  REVOKED: { label: 'Revoked', cls: 'status-muted' },
};

const date = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const ageInDays = (iso: string) => Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);

const splitRegions = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const errorText = (error: unknown, fallback: string) =>
  error instanceof ApiError
    ? [error.message, ...(error.fieldErrors ?? []).map((f) => `${f.path}: ${f.message}`)].join(
        ' · ',
      )
    : fallback;

/**
 * Cloud accounts the platform may discover architecture in (ADR 0005).
 *
 * A connection holds an access key for an IAM user with only the discovery policy below. The key
 * is sent once and never shown again — not to this page, not to anyone. Before it is used, the
 * platform asks IAM what the key can do and refuses it if it can do anything but read metadata,
 * so an administrator's own key is refused rather than stored and used.
 */
export function CloudAccountsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'security-admin';
  const queryClient = useQueryClient();

  const connections = useQuery({
    queryKey: ['cloudConnections'],
    queryFn: api.listCloudConnections,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((c) => c.status === 'PENDING') ? 3000 : false,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['cloudConnections'] });

  const rows = [...(connections.data?.items ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Governance</span>
          <h1>Cloud accounts</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '76ch', marginTop: -8 }}>
        Connect an AWS account so Guardian can map an application&rsquo;s real architecture — what
        exists and how it is wired, never what it holds. The key you give is checked with IAM before
        it is used: a key that can change anything or read data is refused.
      </p>

      <PolicyPanel />

      {isAdmin ? <ConnectForm onDone={refresh} /> : null}

      <section className="card panel table-wrap">
        <h2 className="panel-title">Connections</h2>
        {connections.isLoading ? (
          <p className="muted">Loading…</p>
        ) : connections.isError ? (
          <p className="login-error" role="alert">
            {errorText(connections.error, 'Could not load cloud connections.')}
          </p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p>No cloud accounts connected.</p>
            <p className="muted">
              {isAdmin
                ? 'Follow the steps above, then paste the discovery user’s key.'
                : 'A security administrator connects cloud accounts.'}
            </p>
          </div>
        ) : (
          <ul className="connection-list">
            {rows.map((c) => (
              <ConnectionRow key={c.id} connection={c} isAdmin={isAdmin} onChange={refresh} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PolicyPanel() {
  const [copied, setCopied] = useState(false);
  const policy = JSON.stringify(discoveryPolicyDocument(), null, 2);
  return (
    <section className="card panel">
      <h2 className="panel-title">Before you connect</h2>
      <ol className="steps">
        <li>
          In the AWS account, create an IAM user just for discovery (for example{' '}
          <span className="mono">guardian-discovery</span>) with no console access.
        </li>
        <li>Attach the policy below to it — and nothing else.</li>
        <li>Create an access key for that user, and paste it here.</li>
      </ol>
      <details className="policy-details">
        <summary>
          Discovery policy ({DISCOVERY_PERMISSIONS.length} groups of read-only actions)
        </summary>
        <ul className="policy-purposes">
          {DISCOVERY_PERMISSIONS.map((p) => (
            <li key={p.actions[0]}>
              <strong>{p.purpose}</strong>{' '}
              <span className="mono muted">{p.actions.join(', ')}</span>
            </li>
          ))}
        </ul>
        <pre className="policy-json">{policy}</pre>
      </details>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={() => {
          void navigator.clipboard.writeText(policy).then(() => setCopied(true));
        }}
      >
        {copied ? 'Copied' : 'Copy policy JSON'}
      </button>
    </section>
  );
}

function ConnectForm({ onDone }: { onDone: () => Promise<void> }) {
  const [name, setName] = useState('');
  const [regions, setRegions] = useState('ap-southeast-2, us-east-1');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.createCloudConnection({
        name: name.trim(),
        regions: splitRegions(regions),
        accessKeyId: accessKeyId.trim(),
        secretAccessKey: secretAccessKey.trim(),
      }),
    // Whatever happens, the secret does not stay in the page.
    onSettled: () => setSecretAccessKey(''),
    onSuccess: async () => {
      setName('');
      setAccessKeyId('');
      await onDone();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <section className="card panel">
      <h2 className="panel-title">Connect an AWS account</h2>
      {create.error ? (
        <p className="login-error" role="alert">
          {errorText(create.error, 'Could not connect the account.')}
        </p>
      ) : null}
      <form className="modal-form" onSubmit={submit} autoComplete="off">
        <div className="field-row">
          <div className="field">
            <label htmlFor="cc-name">Name</label>
            <input
              id="cc-name"
              className="input"
              required
              maxLength={80}
              placeholder="InfraGhost dev account"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cc-regions">Regions</label>
            <input
              id="cc-regions"
              className="input mono"
              value={regions}
              onChange={(e) => setRegions(e.target.value)}
            />
            <span className="field-hint">
              Include us-east-1 for CloudFront certificates and WAF.
            </span>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="cc-key">Access key ID</label>
            <input
              id="cc-key"
              className="input mono"
              required
              spellCheck={false}
              placeholder="AKIA…"
              value={accessKeyId}
              onChange={(e) => setAccessKeyId(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cc-secret">Secret access key</label>
            <input
              id="cc-secret"
              className="input mono"
              type="password"
              required
              autoComplete="new-password"
              spellCheck={false}
              value={secretAccessKey}
              onChange={(e) => setSecretAccessKey(e.target.value)}
            />
            <span className="field-hint">Sent once, stored encrypted, never shown again.</span>
          </div>
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Connecting…' : 'Connect and verify'}
          </button>
        </div>
      </form>
    </section>
  );
}

function ConnectionRow({
  connection: c,
  isAdmin,
  onChange,
}: {
  connection: CloudConnection;
  isAdmin: boolean;
  onChange: () => Promise<void>;
}) {
  const [rotating, setRotating] = useState(false);
  const [newKeyId, setNewKeyId] = useState('');
  const [newSecret, setNewSecret] = useState('');

  const act = useMutation({
    mutationFn: async (action: 'verify' | 'revoke' | 'rotate') => {
      if (action === 'verify') return api.verifyCloudConnection(c.id, c.version);
      if (action === 'revoke') return api.revokeCloudConnection(c.id, c.version);
      return api.rotateCloudConnection(c.id, {
        expectedVersion: c.version,
        accessKeyId: newKeyId.trim(),
        secretAccessKey: newSecret.trim(),
      });
    },
    onSettled: () => setNewSecret(''),
    onSuccess: async () => {
      setRotating(false);
      setNewKeyId('');
      await onChange();
    },
  });

  const status = STATUS[c.status];
  const age = c.keyCreatedAt ? ageInDays(c.keyCreatedAt) : null;
  const live = c.status !== 'REVOKED';

  return (
    <li className="connection">
      <div className="connection-head">
        <div>
          <strong>{c.name}</strong> <span className={`status ${status.cls}`}>{status.label}</span>
        </div>
        {isAdmin && live ? (
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={act.isPending || c.status === 'PENDING'}
              onClick={() => act.mutate('verify')}
            >
              Re-check
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setRotating(!rotating)}
            >
              Rotate key
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={act.isPending}
              onClick={() => {
                if (window.confirm(`Revoke ${c.name}? The stored key is deleted immediately.`)) {
                  act.mutate('revoke');
                }
              }}
            >
              Revoke
            </button>
          </div>
        ) : null}
      </div>

      <dl className="kv">
        <dt>Account</dt>
        <dd className="mono">{c.accountId ?? '—'}</dd>
        <dt>Regions</dt>
        <dd className="mono">{c.regions.join(', ')}</dd>
        <dt>Key</dt>
        <dd className="mono">
          …{c.keyFingerprint}
          {age !== null ? (
            <span className={age > KEY_ROTATION_DAYS ? 'sev-high' : 'muted'}>
              {' '}
              · {age} days old{age > KEY_ROTATION_DAYS ? ' — rotate it' : ''}
            </span>
          ) : null}
        </dd>
        <dt>Identity</dt>
        <dd className="mono">{c.principalArn ?? '—'}</dd>
        <dt>Verified</dt>
        <dd>{c.verifiedAt ? date(c.verifiedAt) : '—'}</dd>
      </dl>

      {c.rejectionReasons.length > 0 ? (
        <div className="login-error" role="alert">
          {c.rejectionReasons.map((r) => (
            <p key={r} style={{ margin: '4px 0' }}>
              {r}
            </p>
          ))}
        </div>
      ) : null}

      {c.status === 'VERIFIED' && c.missingActions.length > 0 ? (
        <p className="demo-notice">
          Verified, but these actions are not granted, so what they cover will not be discovered:{' '}
          <span className="mono">{c.missingActions.join(', ')}</span>
        </p>
      ) : null}

      {act.error ? (
        <p className="login-error" role="alert">
          {errorText(act.error, 'That did not work.')}
        </p>
      ) : null}

      {c.status === 'VERIFIED' ? <ProposalsPanel connection={c} /> : null}

      {rotating ? (
        <form
          className="modal-form rotate-form"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            act.mutate('rotate');
          }}
        >
          <div className="field-row">
            <div className="field">
              <label htmlFor={`rk-${c.id}`}>New access key ID</label>
              <input
                id={`rk-${c.id}`}
                className="input mono"
                required
                value={newKeyId}
                onChange={(e) => setNewKeyId(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor={`rs-${c.id}`}>New secret access key</label>
              <input
                id={`rs-${c.id}`}
                className="input mono"
                type="password"
                required
                autoComplete="new-password"
                value={newSecret}
                onChange={(e) => setNewSecret(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={act.isPending}>
              Replace and re-check
            </button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
